import { useRef, useEffect } from 'react'
import { useLocation } from 'react-router'
import { useAuditApi } from './useAuditApi'

/**
 * `useActivityAudit` will audit the activity being performed, such as a page view, etc.
 * The hook ensures that the activity will only be audited exactly once.
 *
 * @param title The title of the activity.
 *
 * @param isReady
 * Indicates that the activity is ready to be audited.
 * This is useful when multiple renders could occur and thus allows auditing of when the component is fully loaded,
 * such as after all API are completed, title is determined, etc.
 */
export const usePageViewAudit = (
  title: string | null | undefined,
  isReady: boolean,
  isSupportPage?: boolean
) => {
  const hasReported = useRef(false)
  const reportedEventId = useRef<number | null>(null)
  const location = useLocation()
  const auditApi = useAuditApi()

  useEffect(() => {
    if (hasReported.current || !isReady || !title) return

    hasReported.current = true

    auditApi
      .recordUserPageViewStarted(title, location.pathname, new Date(), isSupportPage ?? false)
      .then((x) => (reportedEventId.current = x.data))

    return () => {
      if (!reportedEventId.current) return

      auditApi.recordUserPageViewCompleted(reportedEventId.current, new Date())
    }
  }, [isReady == true])
}
